





























import {Component, Prop, Vue} from "vue-property-decorator";
import ItemsList from "@share/web-conferences/components/common/ItemsList.vue";
import Radio from "@share/common/Radio.vue";
import {Debounce} from "vue-debounce-decorator";
import {getDevices as getDevicesHelper} from "@share/web-conferences/helpers/DevicesHelper";
import { setDevicesToStorage } from "@share/web-conferences/helpers/LocalStorageHelper";
import { setSelectedAudioDevice, setSelectedVideoDevice } from "@share/web-conferences/store/actions";
import { LiveKitClientInterface } from "@share/live-kit/interfaces/LiveKitClientInterface";
import { Room } from "livekit-client";

@Component({
  name: "Devices",
  components: {Radio, ItemsList}
})
export default class Device extends Vue {
  static readonly AudioChangeEvent = 'audioChange';
  static readonly VideoChangeEvent = 'videoChange';
  static readonly devicesChanged = 'devicesChanged';

  @Prop({required: true}) client: LiveKitClientInterface;

  videoDevices: MediaDeviceInfo[] = [];
  audioDevices: MediaDeviceInfo[] = [];

  get devices() {
    return [...this.videoDevices, ...this.audioDevices];
  }

  get showVideoList() {
    return this.videoDevices && this.videoDevices.length > 0;
  }

  get showAudioList() {
    return this.audioDevices && this.audioDevices.length > 0;
  }

  get showEmptyMessage() {
    return !this.showVideoList && !this.showAudioList;
  }

  async mounted() {
    await this.getDevices();

    this.$emit(Device.devicesChanged, [this.videoDevices, this.audioDevices]);

    await this.initSelectedAudio();
    await this.initSelectedVideo();

    navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChange);
  }

  async initSelectedAudio() {
    const needToSetAudioDevice = !this.client.audioDeviceId && this.showAudioList;

    if (needToSetAudioDevice) {
      this.client.audioDeviceId = this.audioDevices[0].deviceId;
    }
  }

  async initSelectedVideo() {
    const needToSetVideoDevice = !this.client.videoDeviceId && this.showVideoList;

    if (needToSetVideoDevice) {
      this.client.videoDeviceId = this.videoDevices[0].deviceId;
    }
  }

  async getDevices() {
    [this.videoDevices, this.audioDevices] = await getDevicesHelper();
  }

  findDeviceById(id: string | undefined, type: "video" | "audio" = "video"): MediaDeviceInfo | undefined {
    if (type == "video") {
      return this.videoDevices.find(item => item.deviceId == id);
    }
    return this.audioDevices.find(item => item.deviceId == id);
  }

  @Debounce(300)
  async onDeviceChange() {
    const oldDevices = [...this.devices];
    await this.getDevices();
    await this.renewDevices(oldDevices);
    this.$emit(Device.devicesChanged, [this.videoDevices, this.audioDevices]);
  }

  async renewDevices(oldDevices: MediaDeviceInfo[]) {
    let newSelectedAudio = this.client.audioDeviceId;
    if (!this.findDeviceById(this.client.audioDeviceId, "audio") && this.audioDevices?.length) {
      newSelectedAudio = this.audioDevices[0].deviceId
    }

    let newSelectedVideo = this.client.videoDeviceId;
    if (!this.findDeviceById(this.client.videoDeviceId, "video") && this.videoDevices?.length) {
      newSelectedVideo = this.videoDevices[0].deviceId
    }

    const isAudioInOld = oldDevices.findIndex(item => item.deviceId == newSelectedAudio) >= 0;
    const isVideoInOld = oldDevices.findIndex(item => item.deviceId == newSelectedVideo) >= 0;

    const needToChangeAudio = newSelectedAudio != this.client.audioDeviceId || !isAudioInOld || newSelectedAudio == 'default';
    if (needToChangeAudio) {
      this.client.audioDeviceId = newSelectedAudio;
      await this.onAudioChange();
    }

    const needToChangeVideo = newSelectedVideo != this.client.videoDeviceId || !isVideoInOld;
    if (needToChangeVideo) {
      this.client.videoDeviceId = newSelectedVideo;
      await this.onVideoChange();
    }
  }

  async onAudioChange() {
    this.$emit(Device.AudioChangeEvent, this.client.audioDeviceId);
    await this.client.changeAudioDevice(this.client.audioDeviceId as string);
    this.storeDevices();
  }

  async onVideoChange() {
    this.$emit(Device.VideoChangeEvent, this.client.videoDeviceId);
    await this.client.changeVideoDevice(this.client.videoDeviceId as string);
    this.storeDevices();
  }

  storeDevices() {
      setDevicesToStorage({
          audioDeviceId: this.client.audioDeviceId,
          videoDeviceId: this.client.videoDeviceId,
      });
      this.$store.dispatch(setSelectedVideoDevice, this.client.videoDeviceId);
      this.$store.dispatch(setSelectedAudioDevice, this.client.audioDeviceId);
  }

  destroyed() {
    navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChange);
  }
}
